import React, { useEffect } from "react";
import { useLocation } from "@reach/router";
import { animateScroll } from "react-scroll";
import { useScrollContext } from "../contexts/scroll-context-provider";

const ScrollToTopWrapper = ({ children }) => {
  const location = useLocation();

  // NOTE: BE SURE TO SET THE scrollFlag IN THE PAGE OR COMPONENT WHERE YOU NEED TO SCROLL TO WHEN THE ELEMENT IS RENDERED.
  const { scrollFlag } = useScrollContext();

  const doScrollToHash = (scrollOptions = {}) => {
    const { offset, doSomething, targetElement } = scrollOptions;

    let SMART_OFFSET = offset;
    const headerElement = document.getElementById("primary-nav-header");
    if (headerElement) {
      const headerHeight = headerElement.offsetHeight;
      SMART_OFFSET = offset - headerHeight;
    }

    const options = {
      smooth: true
    };

    // console.log("Scrolling to target element:", targetElement);
    // console.log("Target element offsetTop:", targetElement.offsetTop);
    // console.log("SMART_OFFSET:", SMART_OFFSET);
    animateScroll.scrollTo(targetElement.offsetTop + SMART_OFFSET, options);
    // window.scrollTo(targetElement, 0);
    doSomething();
  };

  /**
   * @typedef {Object} ScrollOptionsData
   * @property {number} offset - value to add offset above or below the element when needed like 20 or -20.
   * @property {number} delay - time in milliseconds used for a timeout before starting the interval to observe for the element we want to scroll.
   * @property {function} doSomething - function called after the scroll process.
   * @property {Object} targetElement - element found in the DOM where we want to scroll to.
   */

  /**
   * Observe the target element and scroll to it when it becomes available just to double check even if the scrollFlag is defined in the right moment.
   *
   * @param {Object} props - The object containing props.
   * @param {string} props.targetId - target id of the element where we want to scroll.
   * @param {ScrollOptionsData} props.scrollOptions - object with the data to handle the scroll behavior.
   */
  const observeAndDoScrollToHash = ({ targetId, scrollOptions }) => {
    // console.log("Scroll options:", scrollOptions);
    setTimeout(() => {
      // console.log("Observing for element:", targetId);
      const interval = setInterval(() => {
        requestAnimationFrame(() => {
          // console.log("Interval triggered for element:", targetId);
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            // console.log("Element found:", targetId);
            const scrollOptionsToPass = {
              offset: scrollOptions?.offset || 0,
              doSomething: scrollOptions?.doSomething || (() => {})
            };
            // console.log("Scroll options:", scrollOptionsToPass);
            doScrollToHash({ ...scrollOptionsToPass, targetElement });

            clearInterval(interval); // Stop the interval once the element is found
          } else {
            // console.warn(`Element with ID "${targetId}" not found, yet...`);
          }
        });
      }, 300); // Keep the interval alive for a while
    }, scrollOptions?.delay || 0);
  };

  const scrollToHash = ({ hash, scrollOptions }) => {
    // Use requestAnimationFrame for more reliable timing
    requestAnimationFrame(() => {
      const targetId = hash.substring(1);

      observeAndDoScrollToHash({ targetId, scrollOptions });
    });
  };

  const dynamicScrollToTop = () => {
    // console.log("Dynamic scroll to top...");
    const options = {
      smooth: false,
      duration: 0,
      isDynamic: true,
      delay: 500
    };

    animateScroll.scrollToTop(options);
  };

  const scrollToTop = () => {
    // console.log("Scrolling to top...");
    const options = {
      duration: 0,
      delay: 0,
      smooth: false
    };

    animateScroll.scrollToTop(options);
  };

  // Custom scroll behavior to FAQ element
  const scrollToFAQ = (hash) => {
    scrollToHash({
      hash,
      scrollOptions: {
        delay: 500
      }
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // console.log("Dynamic scroll flag Scroll To Top Wrapper:", scrollFlag);
    requestAnimationFrame(() => {
      // Set timeout to allow for hash processing
      setTimeout(() => {
        const { pathname, hash } = location;

        const LOCATIONS_DYNAMIC_SCROLL_UP = ["/privacy-center", "/privacy-center/"];

        const foundDynamicLocation = LOCATIONS_DYNAMIC_SCROLL_UP.includes(pathname);

        const HASH_CUSTOM_SCROLL = [{ hash: "faq-accordion", scrollBehavior: scrollToFAQ }];

        if (hash) {
          // console.log("Hash found:", hash);
          const foundHashCustomScroll = HASH_CUSTOM_SCROLL.find((item) => hash.includes(item.hash));

          if (foundHashCustomScroll) {
            // We need to setScrollFlag when we use a custom scroll behavior just to be precise with the element we are scrolling to.
            if (scrollFlag) {
              // console.log("Scroll flag found, scrolling to hash:", hash);
              foundHashCustomScroll.scrollBehavior(hash);
              return; // Stop further checks
            }

            // console.log("scrollFlag not defined yet, waiting for flag to be defined to be able to scroll.");
            return; // Stop further checks
          }

          scrollToHash({ hash });
          return; // Stop further checks
        }

        if (foundDynamicLocation) {
          dynamicScrollToTop();
          return; // Stop further checks
        }

        window.scrollTo(0, 0);
        scrollToTop();
      }, 0);
    });
  }, [location, scrollFlag]); // Re-run effect when location changes

  return <>{children}</>;
};

export default ScrollToTopWrapper;

